import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import LoginLight from './pages/LoginLight';
import RenterDashboard from './pages/RenterDashboard';
import RenterDashboardMinimal from './pages/RenterDashboardMinimal';
import Login from './pages/Login';

function AppLight() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const [isTextFading, setIsTextFading] = useState(false);

  const staticText = "Property Management Made";
  const rotatingWords = [
    "Simple",
    "Efficient",
    "Seamless",
    "Better",
    "Smart"
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTextFading(true);
      setTimeout(() => {
        setTextIndex((prevIndex) => (prevIndex + 1) % rotatingWords.length);
        setIsTextFading(false);
      }, 400);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginLight />} />
        <Route path="/login_old" element={<Login />} />
        <Route path="/portal_old" element={<RenterDashboard />} />
        <Route path="/portal" element={<RenterDashboardMinimal />} />
        <Route path="/" element={
          <div className="min-h-screen bg-white text-gray-900 relative">
            {/* Global Background Gradients */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-blue-100 rounded-full blur-[80px]"></div>
              <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-indigo-100 rounded-full blur-[80px]"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-sky-100 rounded-full blur-[100px]"></div>
            </div>

            {/* Dynamic Header */}
            <header
              className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? 'bg-white/80 backdrop-blur-md shadow-sm' : 'bg-transparent'
              }`}
            >
              <div className="container mx-auto px-4 py-4">
                <nav className="flex items-center justify-between">
                  <Link to="/" className="text-2xl font-bold text-blue-600">
                    OVERHEAD<span className="text-gray-600">PM</span>
                  </Link>
                  <div className="space-x-6">
                    <a href="#home" className="text-gray-600 hover:text-blue-600 transition-colors">Home</a>
                    <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
                    <a href="#analytics" className="text-gray-600 hover:text-blue-600 transition-colors">Analytics</a>
                    <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
                    <Link to="/login" className="text-gray-600 hover:text-blue-600 transition-colors">Login</Link>
                  </div>
                </nav>
              </div>
            </header>

            {/* Hero Section */}
            <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="flex flex-col items-center text-center space-y-8">
                    <div className="inline-block px-4 py-1.5 bg-blue-50 rounded-full border border-blue-100 text-sm font-medium text-blue-600 mb-8">
                      🚀 Just launched BETA
                    </div>
                    <h1 className="text-7xl font-bold tracking-tight">
                      <span className="text-gray-900">
                        Property Management
                      </span>
                      <br />
                      <span className="text-5xl mt-4 block text-gray-600">
                        for the modern era
                      </span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl leading-relaxed">
                      Streamline your property operations with our comprehensive platform. From rent collection to maintenance tracking, we're building the future of property management.
                    </p>
                    <div className="flex w-full max-w-md gap-3">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                      />
                      <button className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all whitespace-nowrap">
                        Join Waitlist
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">Join 1,000+ property managers already on the waitlist</p>
                    <div className="mt-12 flex items-center gap-8 text-gray-600">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">⚡️</span>
                        <span>Lightning Fast</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔒</span>
                        <span>Secure & Private</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🏗️</span>
                        <span>Built to Scale</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-32 relative bg-gray-50">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center mb-20">
                  <h2 className="text-5xl font-bold mb-6 text-gray-900">
                    Built for Modern Property Management
                  </h2>
                  <p className="text-xl text-gray-600">
                    Everything you need to manage and scale your property portfolio, all in one place.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    {
                      title: "Centralized Control",
                      description: "One powerful dashboard to manage your entire portfolio. Track performance, collect rent, and handle maintenance with ease.",
                      icon: "🎯"
                    },
                    {
                      title: "Smart Automation",
                      description: "Automate repetitive tasks, from rent collection to late notices. Save time and reduce operational overhead.",
                      icon: "⚡️"
                    },
                    {
                      title: "Built to Scale",
                      description: "A platform that grows with you. From your first property to your hundredth, we've got you covered.",
                      icon: "📈"
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="p-8 rounded-2xl bg-white border border-gray-100 hover:border-blue-100 hover:shadow-lg transition-all group"
                    >
                      <div className="text-4xl mb-6 transform group-hover:scale-110 transition-transform">
                        {feature.icon}
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Core Features Section */}
            <section className="py-32 relative">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center mb-20">
                  <h2 className="text-5xl font-bold mb-6 text-gray-900">
                    Everything You Need
                  </h2>
                  <p className="text-xl text-gray-600">
                    A complete suite of tools designed for modern property management.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[
                    {
                      title: "Payments & Finance",
                      icon: "💳",
                      features: [
                        "Online Rent Collection",
                        "Automated Late Fees",
                        "Financial Reporting",
                        "Bank Integration"
                      ]
                    },
                    {
                      title: "Document Hub",
                      icon: "📄",
                      features: [
                        "Digital Lease Signing",
                        "Template Library",
                        "Secure Storage",
                        "Easy Sharing"
                      ]
                    },
                    {
                      title: "Maintenance Pro",
                      icon: "🔧",
                      features: [
                        "Work Order System",
                        "Vendor Management",
                        "Photo Documentation",
                        "Cost Tracking"
                      ]
                    },
                    {
                      title: "Smart Communication",
                      icon: "💬",
                      features: [
                        "Tenant Portal",
                        "Mass Announcements",
                        "Chat Support",
                        "Email Templates"
                      ]
                    },
                    {
                      title: "Marketing Suite",
                      icon: "🎯",
                      features: [
                        "Listing Generator",
                        "Virtual Tours",
                        "Lead Management",
                        "Vacancy Tracking"
                      ]
                    },
                    {
                      title: "Insights & Analytics",
                      icon: "📊",
                      features: [
                        "Performance Metrics",
                        "Custom Reports",
                        "Market Analysis",
                        "ROI Calculator"
                      ]
                    }
                  ].map((feature, index) => (
                    <div 
                      key={index} 
                      className="p-8 rounded-2xl bg-white border border-gray-100 hover:border-blue-100 hover:shadow-lg transition-all group"
                    >
                      <div className="flex items-center mb-6">
                        <span className="text-3xl mr-3 transform group-hover:scale-110 transition-transform">{feature.icon}</span>
                        <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                      </div>
                      <ul className="space-y-3">
                        {feature.features.map((item, i) => (
                          <li key={i} className="flex items-center text-gray-600">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Analytics Section */}
            <section id="analytics" className="py-32 relative bg-gray-50">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center mb-20">
                  <h2 className="text-5xl font-bold mb-6 text-gray-900">
                    Data-Driven Growth
                  </h2>
                  <p className="text-xl text-gray-600">
                    Make informed decisions with powerful analytics and insights.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="space-y-6">
                    <div className="p-8 rounded-2xl bg-white border border-gray-100 hover:border-blue-100 hover:shadow-lg transition-all group">
                      <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                        <span className="text-3xl mr-3">📈</span>
                        Portfolio Insights
                      </h3>
                      <ul className="space-y-4">
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Real-time Performance Tracking
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Occupancy & Revenue Analytics
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Expense Monitoring & Forecasting
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Maintenance Cost Analysis
                        </li>
                      </ul>
                    </div>
                    <div className="p-8 rounded-2xl bg-white border border-gray-100 hover:border-blue-100 hover:shadow-lg transition-all group">
                      <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                        <span className="text-3xl mr-3">⚡️</span>
                        Operational Excellence
                      </h3>
                      <ul className="space-y-4">
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Task & Time Management
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Automated Workflows
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Document Processing
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></span>
                          Payment Analytics
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-8 rounded-2xl bg-white border border-gray-100 hover:border-blue-100 hover:shadow-lg transition-all group h-full">
                    <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                      <span className="text-3xl mr-3">🚀</span>
                      Growth Engine
                    </h3>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                      Powerful tools and insights to help you expand your portfolio strategically. Track performance metrics, identify growth opportunities, and make data-driven decisions about your property investments.
                    </p>
                    <div className="space-y-6">
                      <div className="p-4 rounded-xl bg-blue-50">
                        <div className="flex items-center mb-2 group/item">
                          <div className="w-2 h-2 bg-blue-500 rounded-full transition-all duration-500 group-hover/item:bg-gradient-to-r group-hover/item:from-blue-500 group-hover/item:to-indigo-500 group-hover/item:scale-110"></div>
                          <span className="ml-3 text-gray-900 font-semibold">Market Analysis</span>
                        </div>
                        <p className="text-gray-600 text-sm ml-5">
                          Compare your performance against market benchmarks
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-blue-50">
                        <div className="flex items-center mb-2 group/item">
                          <div className="w-2 h-2 bg-blue-500 rounded-full transition-all duration-500 group-hover/item:bg-gradient-to-r group-hover/item:from-blue-500 group-hover/item:to-indigo-500 group-hover/item:scale-110"></div>
                          <span className="ml-3 text-gray-900 font-semibold">ROI Tracking</span>
                        </div>
                        <p className="text-gray-600 text-sm ml-5">
                          Monitor and optimize return on investment across properties
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-blue-50">
                        <div className="flex items-center mb-2 group/item">
                          <div className="w-2 h-2 bg-blue-500 rounded-full transition-all duration-500 group-hover/item:bg-gradient-to-r group-hover/item:from-blue-500 group-hover/item:to-indigo-500 group-hover/item:scale-110"></div>
                          <span className="ml-3 text-gray-900 font-semibold">Growth Planning</span>
                        </div>
                        <p className="text-gray-600 text-sm ml-5">
                          Tools to plan and execute your portfolio expansion
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 relative">
              <div className="container mx-auto px-4">
                <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
                  Simple, Transparent Pricing
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                  {[
                    {
                      name: "Starter",
                      price: "$29",
                      description: "Perfect for getting started",
                      features: [
                        "Up to 25 properties",
                        "Basic reporting",
                        "Email support",
                        "Core features"
                      ]
                    },
                    {
                      name: "Growth",
                      price: "$79",
                      description: "For expanding portfolios",
                      features: [
                        "Up to 100 properties",
                        "Full reporting suite",
                        "Priority support",
                        "All features",
                        "QuickBooks integration"
                      ],
                      popular: true
                    },
                    {
                      name: "Scale",
                      price: "Custom",
                      description: "For larger portfolios",
                      features: [
                        "Unlimited properties",
                        "Custom reporting",
                        "Dedicated support",
                        "API access",
                        "Custom training",
                        "Migration assistance"
                      ]
                    }
                  ].map((plan, index) => (
                    <div
                      key={index}
                      className={`p-8 rounded-lg border relative ${
                        plan.popular
                          ? "border-blue-200 bg-blue-50"
                          : "border-gray-100 bg-white"
                      } hover:shadow-lg transition-all`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm">
                          Most Popular
                        </div>
                      )}
                      <h3 className="text-2xl font-bold mb-2 text-gray-900">{plan.name}</h3>
                      <div className="text-4xl font-bold mb-4 text-gray-900">{plan.price}</div>
                      <p className="text-gray-600 mb-6">{plan.description}</p>
                      <ul className="space-y-3 mb-8">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-gray-600">
                            <span className="mr-2 text-blue-500">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <button
                        className={`w-full py-3 rounded-lg font-bold transition-all ${
                          plan.popular
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-white text-blue-600 border border-blue-200 hover:bg-blue-50"
                        }`}
                      >
                        Get Started
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 relative bg-gray-50">
              <div className="container mx-auto px-4">
                <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
                  Ready to Elevate Your Property Management?
                </h2>
                <div className="max-w-2xl mx-auto">
                  <div className="p-8 rounded-lg border border-gray-100 bg-white">
                    <form className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio Size</label>
                        <select className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900">
                          <option>1-10 properties</option>
                          <option>11-50 properties</option>
                          <option>51-200 properties</option>
                          <option>200+ properties</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        className="w-full px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all"
                      >
                        Get Started
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <footer className="py-12 relative bg-gray-50">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                  <div>
                    <div className="text-2xl font-bold text-blue-600 mb-4">
                      OVERHEAD<span className="text-gray-600">PM</span>
                    </div>
                    <p className="text-gray-600">
                      The future of property management is here.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-bold mb-4">Product</h4>
                    <ul className="space-y-2">
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Pricing</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Integrations</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">API</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-bold mb-4">Company</h4>
                    <ul className="space-y-2">
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">About</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Blog</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Careers</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-bold mb-4">Resources</h4>
                    <ul className="space-y-2">
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Documentation</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Support</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Status</a></li>
                      <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Terms</a></li>
                    </ul>
                  </div>
                </div>
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-col md:flex-row justify-between items-center">
                    <p className="text-gray-500 text-sm">
                      © 2024 Overhead Property Management. All rights reserved.
                    </p>
                    <div className="flex space-x-6 mt-4 md:mt-0">
                      <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Twitter</a>
                      <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">LinkedIn</a>
                      <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">GitHub</a>
                    </div>
                  </div>
                </div>
              </div>
            </footer>
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default AppLight; 